import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { AURA_CLIENT_HEADERS } from '../utils/auraClient';

// API-Basis-URL direkt definieren
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

/**
 * LtiSession Komponente - Verarbeitet den LTI-Launch-Flow
 * 
 * Diese Komponente verarbeitet LTI-Launch-Parameter und führt die Authentifizierung durch.
 * Sie wird nach einem LTI-Launch vom LMS aufgerufen und bietet:
 * - Automatische Benutzerregistrierung bei Erstanmeldung
 * - Single Sign-On (SSO) für bestehende Benutzer
 * - Speicherung von LTI-Kontextinformationen
 * - Weiterleitung zu ProfileSetup (bei Erstanmeldung) oder Dashboard
 */
const LtiSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [processingStep, setProcessingStep] = useState('initialize');

  useEffect(() => {
    const authenticateViaLti = async () => {
      try {
        // Verarbeitungsschritt aktualisieren
        setProcessingStep('extracting_params');
        
        // Parameter aus der URL extrahieren
        const searchParams = new URLSearchParams(location.search);
        const contextId = searchParams.get('contextId');
        const token = searchParams.get('token');
        const ticket = searchParams.get('ticket');
        const errorCode = searchParams.get('error');
        
        // Weitere LTI-Kontextparameter erfassen
        const lmsType = searchParams.get('lms_type') || 'unknown';
        const contextLabel = searchParams.get('context_label') || searchParams.get('context_title') || 'LMS-Kurs';
        const resourceLinkId = searchParams.get('resource_link_id') || '';
        const userRole = searchParams.get('roles') || '';
        const isFirstLogin = searchParams.get('is_first_login') === 'true';
        
        // Speichere alle relevanten LTI-Informationen im localStorage
        if (contextId) localStorage.setItem('ltiContextId', contextId);
        if (lmsType) localStorage.setItem('ltiLmsType', lmsType);
        if (contextLabel) localStorage.setItem('ltiContextLabel', contextLabel);
        if (resourceLinkId) localStorage.setItem('ltiResourceLinkId', resourceLinkId);
        if (userRole) localStorage.setItem('ltiUserRole', userRole);
        
        // Tracking ob dies ein neuer Benutzer ist
        setIsNewUser(isFirstLogin);
        
        // Verarbeitungsschritt aktualisieren
        setProcessingStep('validating');
        
        // Behandle spezifische Fehlercodes, die vom LMS kommen könnten
        if (errorCode) {
          switch (errorCode) {
            case 'invalid_request':
              throw new Error('Ungültige LTI-Anfrage vom LMS. Bitte starte den Launch erneut.');
            case 'unauthorized':
              throw new Error('Keine Berechtigung für den Zugriff auf EachOtter mit diesem LMS-Konto.');
            case 'config_error':
              throw new Error('LTI-Konfigurationsfehler. Bitte wende dich an deine Schuladministration.');
            default:
              throw new Error(`LTI-Fehler: ${errorCode}`);
          }
        }

        // Fehler, wenn weder ein direktes Token noch ein Launch-Ticket vorliegt
        if (!token && (!ticket || !contextId)) {
          console.error('Fehlende Parameter', { hasTicket: !!ticket, contextId });
          throw new Error('Unvollständige LTI-Parameter. Die Authentifizierung konnte nicht abgeschlossen werden.');
        }

        // Verarbeitungsschritt aktualisieren
        setProcessingStep('authenticating');

        // Wenn Token direkt in der URL ist, verwenden wir es (DirectSSO-Flow)
        if (token) {
          // Token speichern und Benutzerinformationen abrufen
          login(token);
          
          // Benutzerinformationen abrufen
          const response = await axios.get(`${API_BASE_URL}/api/v1/auth/me`, {
            headers: {
              ...AURA_CLIENT_HEADERS,
              'Authorization': `Bearer ${token}`
            }
          });
          
          setUser(response.data.user);
          
          // LTI-Kontextinformationen im localStorage speichern
          localStorage.setItem('ltiContextId', contextId);
          
          // Verarbeitungsschritt aktualisieren
          setProcessingStep('sending_welcome');
          
          // Willkommensnachricht an das LMS senden, wenn dies ein neuer LTI-Benutzer ist
          try {
            // LtiNotificationService dynamisch importieren, um Zirkelbezüge zu vermeiden
            const LtiNotificationService = (await import('../services/LtiNotificationService')).default;
            
            if (LtiNotificationService.areNotificationsAvailable() && isFirstLogin) {
              await LtiNotificationService.sendWelcomeNotification(response.data.user.id);
              console.log('Willkommensnachricht an LMS gesendet');
            }
          } catch (notificationError) {
            // Fehler beim Senden der Benachrichtigung nicht blockieren
            console.error('Fehler beim Senden der Willkommensnachricht an LMS:', notificationError);
          }
          
          // Verarbeitungsschritt aktualisieren
          setProcessingStep('redirecting');
          
          // Bei Erstanmeldung zum Profilsetup leiten, sonst zum Dashboard
          if (isFirstLogin) {
            navigate('/profile/setup');
          } else {
            navigate('/dashboard');
          }
          return;
        }

        // Andernfalls Token über die API anfordern (Standard-Flow, ticket-basiert)
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/lti-login`, {
          ticket
        });

        if (response.data.token) {
          // Token speichern und Benutzerinformationen setzen
          login(response.data.token);
          setUser(response.data.user);
          
          // LTI-Kontextinformationen im localStorage speichern
          localStorage.setItem('ltiContextId', contextId);
          localStorage.setItem('isLtiUser', 'true');
          
          // Verarbeitungsschritt aktualisieren
          setProcessingStep('sending_welcome');
          
          // Willkommensnachricht an das LMS senden, wenn dies ein neuer LTI-Benutzer ist
          if (response.data.isNewUser) {
            try {
              // LtiNotificationService dynamisch importieren, um Zirkelbezüge zu vermeiden
              const LtiNotificationService = (await import('../services/LtiNotificationService')).default;
              
              if (LtiNotificationService.areNotificationsAvailable()) {
                await LtiNotificationService.sendWelcomeNotification(response.data.user.id);
                console.log('Willkommensnachricht an LMS gesendet');
              }
            } catch (notificationError) {
              // Fehler beim Senden der Benachrichtigung nicht blockieren
              console.error('Fehler beim Senden der Willkommensnachricht an LMS:', notificationError);
            }
            
            // Neuen Benutzer markieren
            setIsNewUser(true);
          }
          
          // Verarbeitungsschritt aktualisieren
          setProcessingStep('redirecting');
          
          // Bei Erstanmeldung zum Profilsetup leiten, sonst zum Dashboard
          if (response.data.isNewUser || isFirstLogin) {
            navigate('/profile/setup');
          } else {
            navigate('/dashboard');
          }
        } else {
          throw new Error('Token konnte nicht abgerufen werden.');
        }
      } catch (error) {
        console.error('LTI-Authentifizierungsfehler:', error);
        
        // Verarbeitungsschritt aktualisieren
        setProcessingStep('error');
        
        // Kategorisiere Fehlermeldungen für bessere Benutzerführung
        let userFriendlyMessage = 'Bei der LTI-Authentifizierung ist ein Fehler aufgetreten.';
        
        if (error.response) {
          // Fehler von der API
          const status = error.response.status;
          if (status === 401 || status === 403) {
            userFriendlyMessage = 'Authentifizierungsfehler: Keine Berechtigung für den Zugriff auf EachOtter mit diesem Konto.';
          } else if (status === 404) {
            userFriendlyMessage = 'Das Konto wurde nicht gefunden. Möglicherweise ist es nicht mit EachOtter verknüpft.';
          } else if (status === 429) {
            userFriendlyMessage = 'Zu viele Anfragen. Bitte versuche es später erneut.';
          } else if (status >= 500) {
            userFriendlyMessage = 'Server-Fehler: EachOtter ist derzeit nicht verfügbar. Bitte versuche es später erneut.';
          }
        } else if (error.message) {
          // Direkte Fehlermeldung verwenden, wenn vorhanden
          userFriendlyMessage = error.message;
        }
        
        setError(userFriendlyMessage);
      } finally {
        setLoading(false);
      }
    };

    authenticateViaLti();
  }, [location, login, navigate, setUser]);

  // Lade-Anzeige mit detailliertem Fortschritt
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface-raised">
        <img 
          src="/logo.png" 
          alt="EachOtter Logo" 
          width={128}
          height={128}
          className="w-32 mb-6" 
        />
        
        <div className="w-full max-w-md">
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-info bg-info-soft">
                  {
                    processingStep === 'initialize' ? 'Initialisiere…' :
                    processingStep === 'extracting_params' ? 'Extrahiere Parameter…' :
                    processingStep === 'validating' ? 'Validiere Anfrage…' :
                    processingStep === 'authenticating' ? 'Authentifiziere…' :
                    processingStep === 'sending_welcome' ? 'Sende Willkommensnachricht…' :
                    processingStep === 'redirecting' ? 'Leite weiter…' : 'Verarbeite…'
                  }
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-info">
                  {
                    processingStep === 'initialize' ? '20%' :
                    processingStep === 'extracting_params' ? '40%' :
                    processingStep === 'validating' ? '60%' :
                    processingStep === 'authenticating' ? '80%' :
                    processingStep === 'sending_welcome' ? '90%' :
                    processingStep === 'redirecting' ? '95%' : '50%'
                  }
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-info-soft">
              <div 
                className="animate-pulse shadow-none flex flex-col text-center whitespace-nowrap text-info-foreground justify-center bg-info"
                style={{ 
                  width: processingStep === 'initialize' ? '20%' :
                         processingStep === 'extracting_params' ? '40%' :
                         processingStep === 'validating' ? '60%' :
                         processingStep === 'authenticating' ? '80%' :
                         processingStep === 'sending_welcome' ? '90%' :
                         processingStep === 'redirecting' ? '95%' : '50%' 
                }}
              ></div>
            </div>
          </div>
        </div>
        
        <h2 className="text-xl font-medium mt-4 text-foreground">
          {isNewUser ? 'Dein EachOtter-Konto wird erstellt…' : 'LTI-Verbindung wird hergestellt…'}
        </h2>
        <p className="text-muted-foreground mt-2 text-center max-w-md">
          {isNewUser 
            ? 'Dein LMS-Konto wird mit EachOtter verbunden. Das dauert nur einen Moment.'
            : 'Du wirst automatisch angemeldet. Einen Moment bitte…'}
        </p>
      </div>
    );
  }

  // Fehleranzeige bei Problemen
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-surface-raised">
        <img 
          src="/logo.png" 
          alt="EachOtter Logo" 
          width={128}
          height={128}
          className="w-32 mb-6" 
        />
        <div className="w-full max-w-md p-4 mb-4 bg-danger-soft border border-danger/30 rounded-lg text-foreground">
          <h3 className="font-bold mb-2">LTI-Verbindungsfehler</h3>
          <p>{error}</p>
        </div>
        <div className="w-full max-w-md mb-4 p-4 bg-info-soft border border-info/30 rounded-lg text-info">
          <h4 className="font-medium mb-1">Lösungsvorschläge:</h4>
          <ul className="list-disc pl-5 text-sm">
            <li>Lade die Seite neu</li>
            <li>Starte den Launch vom LMS erneut</li>
            <li>Prüfe, ob dein Konto für EachOtter freigegeben ist</li>
            <li>Wende dich an deine Schuladministration oder an support@eachotter.de</li>
          </ul>
        </div>
        <div className="flex space-x-4">
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-cta text-cta-foreground rounded-xl hover:bg-cta-hover transition-colors"
          >
            Neu laden
          </button>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-surface-raised text-foreground-2 rounded hover:bg-foreground/10 transition-colors"
          >
            Zurück zum LMS
          </button>
        </div>
      </div>
    );
  }

  // Diese Komponente rendert keine eigene UI bei erfolgreichem Prozess
  return null;
};

export default LtiSession; 